import type { Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import {
  CognitoUserCreateInput,
  CognitoUserCountInput,
  CognitoUserSameEmailCountInput,
  ShopListInput,
  GetCognitoUserQuery,
  GetCognitoUserDocument,
  CreateCognitoUserMutation,
  UpdateCognitoUserMutation,
  UpdateCognitoUserPasswordMutation,
} from "@/generated/graphql";
import { getWordList } from "@/common/graphql/expression";
import { getChainList, getChainInfo } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import {
  createCognitoUser,
  getCognitoUserCount,
  updateCognitoUser,
  updateCognitoUserPassword,
  getCognitoUserSameEmailCount,
} from "@/common/graphql/cognito";
import { generateApolloClient } from "@/common/apolloClientUtil";
import type { ChainItem, ShopItem } from "../types";

/** オペレータ登録 API通信に必要な依存をまとめた型。 */
interface UseOperatorRegisterApiDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
}

/**
 * オペレータ登録 API通信を扱う composable。
 * GraphQL通信の一元管理、エラーハンドリングを行う。
 */
export const useOperatorRegisterApi = (deps: UseOperatorRegisterApiDeps) => {
  const { errdialog, errMessage } = deps;

  /**
   * 文言データを取得する。
   */
  async function getWordListData(
    chainCode: string,
    screenId: string,
    customerCode: string
  ): Promise<Record<string, string>> {
    return await getWordList(chainCode, screenId, customerCode);
  }

  /**
   * チェーンリストを取得する。
   */
  async function getChainListData(
    customerCode: string
  ): Promise<ChainItem[] | null | undefined> {
    return await getChainList(customerCode);
  }

  /**
   * 店舗リストを取得する。
   */
  async function getShopListData(
    input: ShopListInput
  ): Promise<ShopItem[] | null | undefined> {
    return await getShopList(input);
  }

  /**
   * チェーン情報を取得する。
   */
  async function getChainInfoData(
    customerCode: string,
    chainCode: string
  ) {
    return await getChainInfo(customerCode, chainCode);
  }

  /**
   * オペレータ情報を取得する（編集時）。
   */
  async function getCognitoUserData(
    input: {
      chain_code: string;
      customer_code: string;
      user_id: string;
    },
    logInput: {
      area_code: number;
      detail: string;
      display_category: string;
      operation: string;
      user_id: string;
    }
  ): Promise<ApolloQueryResult<GetCognitoUserQuery>> {
    const apolloClient = await generateApolloClient();
    return await apolloClient.query<GetCognitoUserQuery>({
      query: GetCognitoUserDocument,
      variables: {
        input,
        logInput,
      },
    });
  }

  /**
   * オペレータID重複カウントを取得する。
   */
  async function getCognitoUserCountData(
    input: CognitoUserCountInput
  ): Promise<number> {
    return await getCognitoUserCount(input);
  }

  /**
   * Email重複カウントを取得する。
   */
  async function getCognitoUserSameEmailCountData(
    input: CognitoUserSameEmailCountInput
  ): Promise<number> {
    return await getCognitoUserSameEmailCount(input);
  }

  /**
   * オペレータを新規登録する。
   */
  async function createCognitoUserData(
    input: CognitoUserCreateInput
  ): Promise<CreateCognitoUserMutation | null | undefined> {
    return await createCognitoUser(input);
  }

  /**
   * オペレータ情報を更新する。
   */
  async function updateCognitoUserData(
    input: {
      user_id: string;
      customer_code: string;
      chain_code: string;
      shop_code: string;
      group: string;
      email: string;
      phone_number: string;
      name: string;
      auth_type: string;
      personal_info: string;
      personal_info_dl: string;
      operator_column_email: number;
      operator_column_tel: number;
    },
    logInput: {
      area_code: string;
      detail: string;
      display_category: string;
      operation: string;
      user_id: string;
    }
  ): Promise<UpdateCognitoUserMutation | null | undefined> {
    return await updateCognitoUser(input, logInput);
  }

  /**
   * オペレータパスワードを更新する。
   */
  async function updateCognitoUserPasswordData(
    input: {
      chain_code: string;
      customer_code: string;
      temporary: number;
      user_id: string;
      new_password: string;
    },
    logInput: {
      area_code: string;
      detail: string;
      display_category: string;
      operation: string;
      user_id: string;
    }
  ): Promise<UpdateCognitoUserPasswordMutation | null | undefined> {
    return await updateCognitoUserPassword(input, logInput);
  }

  return {
    getWordListData,
    getChainListData,
    getShopListData,
    getChainInfoData,
    getCognitoUserData,
    getCognitoUserCountData,
    getCognitoUserSameEmailCountData,
    createCognitoUserData,
    updateCognitoUserData,
    updateCognitoUserPasswordData,
  };
};
